import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, ActivationEnd, Data, NavigationEnd, Router } from '@angular/router';
import { BreadcrumbItem } from '@scalefast/core';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BreadcrumbHelper } from '../helpers/breadcrumb.helper';

@Injectable({
  providedIn: 'root',
})
export class CustomBreadcrumbService {
  private readonly _breadcrumbs$ = new BehaviorSubject<BreadcrumbItem[]>([]);
  readonly breadcrumbs$ = this._breadcrumbs$.asObservable();
  private breadcrumbs: BreadcrumbItem[] = [];

  constructor(private router: Router) {
    const root = this.router.routerState.snapshot.root;
    this.updateBreadcrumb(root); // The breadcrumb is updated on page reload

    this.router.events.pipe(filter((event) => event instanceof ActivationEnd)).subscribe(() => {
      const root = this.router.routerState.snapshot.root;
      this.updateBreadcrumb(root);
    });
  }

  private updateBreadcrumb(root: ActivatedRouteSnapshot): void {
    this.generateBreadcrumb(root);
    this._breadcrumbs$.next(this.breadcrumbs);
  }

  /**
   * Generate the breadcrumb from the route
   * @param route ActivatedRouteSnapshot
   * @returns void
   */
  private generateBreadcrumb(route: ActivatedRouteSnapshot) {
    this.breadcrumbs = [];
    let url = undefined;
    let customRoutes: boolean = false;

    // route.firstChild is the first child of the root route (e.g. /home)
    while (route.firstChild) {
      // Quick solution to avoid multiple iteration on menu data
      if (route.firstChild.data?.page?.extraData?.customBreadcrumbs) {
        this.breadcrumbs = route.firstChild.data.page.extraData?.customBreadcrumbs;
        customRoutes = true;
        return;
      }
      if (route.firstChild.routeConfig?.path) {
        url = BreadcrumbHelper.getUrl(route.firstChild, url);
      }
      route = route.firstChild;
    }

    if (customRoutes) {
      return;
    }

    // Get the breadcrumb data from the route
    const { breadcrumb, page } = route.data ?? undefined;

    // Escape if there is no breadcrumb data or the breadcrumb is set to '**'
    if (!url || url === '**' || !breadcrumb || !page) {
      return;
    }

    const urlSegments = url.split('/');
    const breadcrumbData = breadcrumb ? BreadcrumbHelper.getLabel(route.data) : undefined;

    // Add the breadcrumbs to the array
    urlSegments.reduce((acc, segment, index, arr) => {
      const path = `${acc}/${segment}`;
      const label = index === arr.length - 1 && breadcrumbData ? breadcrumbData : segment;

      this.breadcrumbs.push({ label, path });

      return path;
    }, '');

    // Sanitize the breadcrumb
    this.sanitizeBreadcrumb();
  }

  /**
   * Sanitize the breadcrumb
   */
  private sanitizeBreadcrumb() {
    this.breadcrumbs = BreadcrumbHelper.removeOrphanedBreadcrumb(this.breadcrumbs);
    this.breadcrumbs = BreadcrumbHelper.removeColonsFromBreadcrumb(this.breadcrumbs);
    this.breadcrumbs = BreadcrumbHelper.removeHyphensFromBreadcrumb(this.breadcrumbs);
  }
}
